import React from "react";

/**
 * BookCard — premium book cover display.
 * If `image` is provided, renders the actual cover photo.
 * Otherwise renders a faithful SVG mock matching the Sanity cover style (black + gold + serif SANITY + WORD).
 */
export default function BookCard({
  num,
  title,
  word,            // BODY | MIND | SPIRIT (used for SVG fallback)
  image,           // optional path to actual cover image
  sub,             // small sub-label below word ("Structure", etc.)
  status,          // optional status badge text
  testid,
}) {
  return (
    <div className="reveal" data-testid={testid}>
      <div className="relative aspect-[3/4] overflow-hidden book group">
        {image ? (
          <img
            src={image}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover img-tint"
            loading="lazy"
          />
        ) : (
          <SVGCover word={word} />
        )}
        {status && (
          <div className="absolute top-4 right-4 bg-[#B89B5E] text-[#1A1A1A] text-[0.6rem] uppercase tracking-[0.22em] px-2.5 py-1 font-medium">
            {status}
          </div>
        )}
      </div>
      <div className="mt-6">
        <p className="text-[0.65rem] uppercase tracking-[0.32em] text-[#9A7E47]">
          {num}
        </p>
        <h3 className="mt-3 font-serif text-2xl md:text-3xl text-[#1A1A1A] tracking-tight leading-tight">
          {title}
        </h3>
        {sub && (
          <p className="mt-2 text-[#707070] text-sm italic font-serif">{sub}</p>
        )}
      </div>
    </div>
  );
}

function SVGCover({ word = "MIND" }) {
  return (
    <svg
      viewBox="0 0 300 400"
      xmlns="http://www.w3.org/2000/svg"
      className="absolute inset-0 w-full h-full"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <linearGradient id="bookGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0c0c0c" />
          <stop offset="50%" stopColor="#0a0a0a" />
          <stop offset="100%" stopColor="#161616" />
        </linearGradient>
        <linearGradient id="goldShine" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#E5C887" />
          <stop offset="50%" stopColor="#C8A45E" />
          <stop offset="100%" stopColor="#9E7A42" />
        </linearGradient>
      </defs>

      <rect width="300" height="400" fill="url(#bookGrad)" />

      {/* triangle monogram */}
      <g transform="translate(150 75)">
        <path d="M0 -32 L36 30 L-36 30 Z" stroke="url(#goldShine)" strokeWidth="2" fill="none" />
        <path d="M0 -20 L26 24 L-26 24 Z" stroke="url(#goldShine)" strokeWidth="1.4" fill="none" />
        <path d="M0 -8 L16 18 L-16 18 Z" stroke="url(#goldShine)" strokeWidth="1.1" fill="none" />
      </g>

      {/* SANITY wordmark */}
      <text
        x="150"
        y="165"
        textAnchor="middle"
        fontFamily="Fraunces, Georgia, serif"
        fontSize="34"
        letterSpacing="6"
        fill="url(#goldShine)"
        fontWeight="500"
      >
        SANITY
      </text>

      <text
        x="150"
        y="185"
        textAnchor="middle"
        fontFamily="DM Sans, sans-serif"
        fontSize="7"
        letterSpacing="4"
        fill="#C8A45E"
      >
        BODY · MIND · SPIRIT
      </text>

      <line x1="125" y1="200" x2="175" y2="200" stroke="#C8A45E" strokeWidth="0.6" />

      {/* USER MANUAL FOR THE */}
      <text
        x="150"
        y="248"
        textAnchor="middle"
        fontFamily="DM Sans, sans-serif"
        fontSize="13"
        letterSpacing="4"
        fill="#F4F2EC"
        fontWeight="400"
      >
        USER MANUAL
      </text>
      <line x1="115" y1="262" x2="135" y2="262" stroke="#C8A45E" strokeWidth="0.5" />
      <text
        x="150"
        y="266"
        textAnchor="middle"
        fontFamily="DM Sans, sans-serif"
        fontSize="11"
        letterSpacing="4"
        fill="#F4F2EC"
      >
        FOR THE
      </text>
      <line x1="165" y1="262" x2="185" y2="262" stroke="#C8A45E" strokeWidth="0.5" />

      {/* WORD (big) */}
      <text
        x="150"
        y="312"
        textAnchor="middle"
        fontFamily="DM Sans, sans-serif"
        fontSize="38"
        letterSpacing="3"
        fill="#F4F2EC"
        fontWeight="300"
      >
        {word}
      </text>

      <line x1="120" y1="338" x2="180" y2="338" stroke="#C8A45E" strokeWidth="0.6" />

      {/* author */}
      <text
        x="150"
        y="368"
        textAnchor="middle"
        fontFamily="DM Sans, sans-serif"
        fontSize="9"
        letterSpacing="4"
        fill="#C8A45E"
      >
        EVA PETKOFF
      </text>
    </svg>
  );
}
